import styled from "@emotion/styled";
import { IconLetter } from "@kompare/kompare-ui-library";
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { i18n, mediaQuery, persistor, setProgressBarPercentage, showMessage, useAppDispatch, useAppSelector } from 'src/common';
import { usePaymentAttemptMutation, useSetWsPayResponseMutation, } from 'src/common/api/apollo/additional';
import { Button, Flex, StatusMessage } from 'src/components';
import config from 'src/config';
import { colors, footer, header } from 'src/theme';

const ButtonContainerStyle = styled.div({
  marginTop: 32,
  textAlign: 'center',
});

const FlexStyle = styled(Flex)({
  minHeight: `calc(100vh - ${footer.mobileHeight + header.mobileHeight + 80}px)`,

  [mediaQuery('mobile')]: {
    minHeight: `calc(100vh - ${footer.height + header.mobileHeight + 80}px)`,
  },

  [mediaQuery('laptopL')]: {
    minHeight: `calc(100vh - ${footer.height + header.height + 80}px)`,
  },
});

type UrlParams = {
  [key: string]: string;
};

const keyMapping: { [key: string]: string } = {
  CustomerFirstname: 'wsPayCustomerFirstName',
  CustomerSurname: 'wsPayCustomerLastName',
  ShoppingCartID: 'wsPayShoppingCartId',
  DateTime: 'wsPayDateTime',
  Amount: 'wsPayAmount',
  STAN: 'wsPaySTAN',
  Partner: 'wsPayPartner',
  WsPayOrderId: 'wsPayOrderId',
  PaymentType: 'wsPayPaymentType',
  CreditCardNumber: 'wsPayCreditCardNumber',
  PaymentPlan: 'wsPayPaymentPlan',
  Success: 'wsPaySuccess',
  ApprovalCode: 'wsPayApprovalCode',
  Signature: 'wsPayConfirmationSignature',
};

const CheckoutSuccessAdditionalPage = () => {
  const dispatch = useAppDispatch();

  const offerId = useAppSelector((store) => store.model.offer.offerId);

  const [params, setParams] = useState<UrlParams | null>(null);

  const location = useLocation();

  useEffect(() => {
    if (params) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'gtmt.purchase',
        trackingData: {
          category: 'Zdravstveno osiguranje',
          revenue: params.wsPayAmount
        },
      });
    }
  }, [location, params]);

  useEffect(() => {
    dispatch(setProgressBarPercentage(0));

    const queryParams = new URLSearchParams(window.location.search);
    const paramsObject: UrlParams = Array.from(queryParams.entries()).reduce((acc, [key, value]) => {
      if (keyMapping[key]) {
        const newKey = keyMapping[key];
        acc[newKey] = value;
      }
      return acc;
    }, {} as UrlParams);
    setParams(paramsObject);

    persistor.purge();
  }, []);

  const [paymentAttemptMutation] = usePaymentAttemptMutation();
  const [setWsPayResponseMutation] = useSetWsPayResponseMutation();

  useEffect(() => {
    if (offerId && params) {
      paymentAttemptMutation({
        variables: { id: offerId }
      });

      setWsPayResponseMutation({
        variables: { id: offerId, input: { ...params } }
      }).catch(() => {
        dispatch(showMessage({ message: i18n.t('pages.checkoutSuccessPage.error'), type: 'error', duration: 10000000 }));
      });
    }
  }, [params, offerId, paymentAttemptMutation, setWsPayResponseMutation]);

  const navigateToHome = () => {
    window.location.href = config.REACT_APP_KOMPARE_HOME!;
  };

  const Buttons = () => (
    <ButtonContainerStyle>
      <Button btnType='textBlack' onClick={navigateToHome}>
        {i18n.t('pages.checkoutCongratulationsPage.returnToHomeButton')}
      </Button>
    </ButtonContainerStyle>
  );

  return (
    <FlexStyle justify='center' align='center'>
      <StatusMessage buttons={<Buttons />} icon={<IconLetter color={colors.icon4} />} title={i18n.t('pages.checkoutCongratulationsPage.title')} subtitle={i18n.t('pages.checkoutCongratulationsPage.subtitle')} />
    </FlexStyle>
  );
}

export default CheckoutSuccessAdditionalPage;